const STYLABLE_CONTROL_SELECTOR =
  'input, textarea, select, [data-input-type="editor"]';
const SELECTED_CONTROL_ATTRIBUTE = "data-editor-selected-control";
const SELECTED_WRAPPER_ATTRIBUTE = "data-editor-selected-wrapper";
const FIT_TEXT_ATTRIBUTE = "data-fit-text";
const FIT_TEXT_MIN_WIDTH = 64;
const FIT_TEXT_HORIZONTAL_PADDING = 28;

const FONT_SIZE_MAP: Record<string, string> = {
  "1": "10px",
  "2": "12px",
  "3": "16px",
  "4": "18px",
  "5": "24px",
  "6": "32px",
  "7": "48px"
};

const isElementNode = (node: Node): node is HTMLElement =>
  node.nodeType === Node.ELEMENT_NODE;

const getClosestStylableControl = (target: HTMLElement | null) => {
  if (!target) return null;

  const direct = target.closest(STYLABLE_CONTROL_SELECTOR) as HTMLElement | null;
  if (direct) return direct;

  const wrapper = target.closest(".wrapper") as HTMLElement | null;
  if (!wrapper) return null;

  return wrapper.querySelector(STYLABLE_CONTROL_SELECTOR) as HTMLElement | null;
};

const getControlWrapper = (control: HTMLElement) =>
  control.closest(".wrapper") as HTMLElement | null;

const getFitTextTargetElement = (control: HTMLElement) =>
  getControlWrapper(control) ?? control;

const getControlTextValue = (control: HTMLElement) => {
  if (control instanceof HTMLInputElement) {
    return control.value || control.getAttribute("value") || control.placeholder || "";
  }

  if (control instanceof HTMLTextAreaElement) {
    return control.value || control.textContent || control.getAttribute("value") || control.placeholder || "";
  }

  if (control instanceof HTMLSelectElement) {
    const selectedOption = control.selectedOptions?.[0];
    return selectedOption?.textContent || control.value || control.textContent || "";
  }

  return control.textContent || control.getAttribute("value") || control.getAttribute("placeholder") || "";
};

const measureTextWidth = (text: string, font: string) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) return 0;

  context.font = font;
  return context.measureText(text || " ").width;
};

const getComputedFontShorthand = (element: HTMLElement) => {
  const styles = window.getComputedStyle(element);
  return `${styles.fontStyle} ${styles.fontVariant} ${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
};

export const isFitTextEnabled = (control: HTMLElement) =>
  control.getAttribute(FIT_TEXT_ATTRIBUTE) === "true";

export const applyFitTextToControl = (control: HTMLElement) => {
  const target = getFitTextTargetElement(control);
  const text = getControlTextValue(control);
  const font = getComputedFontShorthand(control);
  const textWidth = measureTextWidth(text, font);
  const nextWidth = Math.max(Math.ceil(textWidth + FIT_TEXT_HORIZONTAL_PADDING), FIT_TEXT_MIN_WIDTH);

  target.style.display = "inline-block";
  target.style.width = `${nextWidth}px`;

  if (target !== control) {
    control.style.width = "100%";
  }

  return nextWidth;
};

export const setFitTextEnabled = (
  control: HTMLElement,
  enabled: boolean
) => {
  const target = getFitTextTargetElement(control);

  if (enabled) {
    control.setAttribute(FIT_TEXT_ATTRIBUTE, "true");
    target.setAttribute(FIT_TEXT_ATTRIBUTE, "true");
    applyFitTextToControl(control);
    return;
  }

  control.removeAttribute(FIT_TEXT_ATTRIBUTE);
  target.removeAttribute(FIT_TEXT_ATTRIBUTE);
};

export const updateFitTextForControl = (control: HTMLElement) => {
  if (!isFitTextEnabled(control)) return;

  applyFitTextToControl(control);
};

const getNodeIndex = (node: Node) => {
  const parent = node.parentNode;
  if (!parent) return -1;

  return Array.prototype.indexOf.call(parent.childNodes, node);
};

const getStylableControlFromNode = (node: Node | null) => {
  if (!node) return null;

  if (node.nodeType === Node.ELEMENT_NODE) {
    return getClosestStylableControl(node as HTMLElement);
  }

  return getClosestStylableControl((node as ChildNode).parentElement);
};

const getBoundaryNeighborNode = (
  range: Range,
  editorRoot: HTMLDivElement,
  direction: "previous" | "next"
) => {
  let container: Node | null = range.startContainer;
  let offset = range.startOffset;

  while (container) {
    if (container.nodeType === Node.TEXT_NODE) {
      const textLength = container.textContent?.length ?? 0;
      const isAtBoundary = direction === "previous" ? offset === 0 : offset === textLength;

      if (!isAtBoundary) {
        return null;
      }

      const sibling =
        direction === "previous"
          ? container.previousSibling
          : container.nextSibling;

      if (sibling) return sibling;

      const parentNode: Node | null = container.parentNode;
      if (!parentNode || container === editorRoot) return null;

      const index = getNodeIndex(container);
      if (index < 0) return null;

      offset = direction === "previous" ? index : index + 1;
      container = parentNode;
      continue;
    }

    if (container.nodeType === Node.ELEMENT_NODE) {
      const children = container.childNodes;

      if (direction === "previous" && offset > 0) {
        return children[offset - 1] ?? null;
      }

      if (direction === "next" && offset < children.length) {
        return children[offset] ?? null;
      }

      if (container === editorRoot) {
        return null;
      }

      const parentNode: Node | null = container.parentNode;
      if (!parentNode) return null;

      const index = getNodeIndex(container);
      if (index < 0) return null;

      offset = direction === "previous" ? index : index + 1;
      container = parentNode;
      continue;
    }

    const parentNode: Node | null = container.parentNode;
    if (!parentNode || container === editorRoot) return null;

    const index = getNodeIndex(container);
    if (index < 0) return null;

    offset = direction === "previous" ? index : index + 1;
    container = parentNode;
  }

  return null;
};

const getControlNearCollapsedRange = (
  range: Range,
  editorRoot: HTMLDivElement
) => {
  if (!range.collapsed) return null;

  const previousNode = getBoundaryNeighborNode(range, editorRoot, "previous");
  const previousControl = getStylableControlFromNode(previousNode);
  if (previousControl && editorRoot.contains(previousControl)) {
    return previousControl;
  }

  const nextNode = getBoundaryNeighborNode(range, editorRoot, "next");
  const nextControl = getStylableControlFromNode(nextNode);
  if (nextControl && editorRoot.contains(nextControl)) {
    return nextControl;
  }

  return null;
};

const markSelectedControl = (
  editorRoot: HTMLDivElement,
  control: HTMLElement
) => {
  const selected = editorRoot.querySelector(
    `[${SELECTED_CONTROL_ATTRIBUTE}="true"]`
  ) as HTMLElement | null;

  if (selected && selected !== control) {
    selected.removeAttribute(SELECTED_CONTROL_ATTRIBUTE);
  }

  const selectedWrapper = editorRoot.querySelector(
    `[${SELECTED_WRAPPER_ATTRIBUTE}="true"]`
  ) as HTMLElement | null;

  const wrapper = control.closest(".wrapper") as HTMLElement | null;
  if (selectedWrapper && selectedWrapper !== wrapper) {
    selectedWrapper.removeAttribute(SELECTED_WRAPPER_ATTRIBUTE);
  }

  if (wrapper) {
    wrapper.setAttribute(SELECTED_WRAPPER_ATTRIBUTE, "true");
  }

  control.setAttribute(SELECTED_CONTROL_ATTRIBUTE, "true");
};

const getSelectionRangeInEditorRoot = (editorRoot: HTMLDivElement | null) => {
  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0 || !editorRoot) {
    return null;
  }

  const range = selection.getRangeAt(0);
  if (!editorRoot.contains(range.commonAncestorContainer)) {
    return null;
  }

  return range;
};

const resolvePreferredControlTarget = (
  editorRef: React.RefObject<HTMLDivElement>,
  range: Range | null
) => {
  const selectedControl = getSelectedEditorControl(editorRef);
  if (selectedControl) return selectedControl;

  if (range && range.collapsed && editorRef.current) {
    return getControlNearCollapsedRange(range, editorRef.current);
  }

  return null;
};

const getDecorationTokens = (value: string) =>
  value
    .split(" ")
    .map((item) => item.trim().toLowerCase())
    .filter((item) => item && item !== "none");

const toggleDecoration = (element: HTMLElement, token: "underline" | "line-through") => {
  const currentDecoration =
    element.style.textDecorationLine ||
    window.getComputedStyle(element).textDecorationLine ||
    "";

  const tokens = new Set(getDecorationTokens(currentDecoration));

  if (tokens.has(token)) {
    tokens.delete(token);
  } else {
    tokens.add(token);
  }

  element.style.textDecorationLine = tokens.size
    ? Array.from(tokens).join(" ")
    : "none";
};

const isBoldWeight = (fontWeight: string) => {
  if (fontWeight === "bold") return true;

  const parsed = Number.parseInt(fontWeight, 10);
  return Number.isFinite(parsed) && parsed >= 600;
};

const runNativeCommand = (command: string, value?: string) => {
  if (typeof value === "undefined") {
    document.execCommand(command, false);
    return;
  }

  document.execCommand(command, false, value);
};

const applyStyleOnControl = (
  element: HTMLElement,
  command: string,
  value?: string
) => {
  switch (command) {
    case "bold": {
      const current = window.getComputedStyle(element).fontWeight;
      element.style.fontWeight = isBoldWeight(current) ? "400" : "700";
      return true;
    }
    case "italic": {
      const current = window.getComputedStyle(element).fontStyle;
      element.style.fontStyle = current === "italic" ? "normal" : "italic";
      return true;
    }
    case "underline": {
      toggleDecoration(element, "underline");
      return true;
    }
    case "strikeThrough": {
      toggleDecoration(element, "line-through");
      return true;
    }
    case "foreColor": {
      if (!value) return false;
      element.style.color = value;
      return true;
    }
    case "backColor":
    case "hiliteColor": {
      if (!value) return false;
      element.style.backgroundColor = value;
      return true;
    }
    case "fontSize": {
      if (!value) return false;
      element.style.fontSize = FONT_SIZE_MAP[value] ?? value;
      return true;
    }
    case "fontName": {
      if (typeof value === "undefined") return false;
      element.style.fontFamily = value;
      return true;
    }
    case "justifyLeft": {
      element.style.textAlign = "left";
      return true;
    }
    case "justifyCenter": {
      element.style.textAlign = "center";
      return true;
    }
    case "justifyRight": {
      element.style.textAlign = "right";
      return true;
    }
    case "justifyFull": {
      element.style.textAlign = "justify";
      return true;
    }
    default:
      return false;
  }
};

const getAlignState = (textAlign: string, command: string) => {
  const align = textAlign.toLowerCase();

  if (command === "justifyLeft") return align === "left" || align === "start";
  if (command === "justifyCenter") return align === "center";
  if (command === "justifyRight") return align === "right" || align === "end";
  if (command === "justifyFull") return align === "justify";

  return false;
};

export const clearSelectedEditorControl = (editorRoot: HTMLDivElement | null) => {
  if (!editorRoot) return;

  const selected = editorRoot.querySelector(
    `[${SELECTED_CONTROL_ATTRIBUTE}="true"]`
  ) as HTMLElement | null;

  const selectedWrapper = editorRoot.querySelector(
    `[${SELECTED_WRAPPER_ATTRIBUTE}="true"]`
  ) as HTMLElement | null;

  selected?.removeAttribute(SELECTED_CONTROL_ATTRIBUTE);
  selectedWrapper?.removeAttribute(SELECTED_WRAPPER_ATTRIBUTE);
};

// export const setSelectedEditorControl = (
//   editorRoot: HTMLDivElement | null,
//   target: EventTarget | null
// ) => {
//   if (!editorRoot) return;

//   clearSelectedEditorControl(editorRoot);

//   if (!(target instanceof HTMLElement)) return;

//   const control = getClosestStylableControl(target);
//   if (!control || !editorRoot.contains(control)) return;

//   markSelectedControl(editorRoot, control);
// };

export const setSelectedEditorControl = (
  editorRoot: HTMLDivElement | null,
  target: EventTarget | null
) => {
  if (!editorRoot) return;

  clearSelectedEditorControl(editorRoot);

  if (!(target instanceof HTMLElement)) return;

  const control = getClosestStylableControl(target);
  if (!control || !editorRoot.contains(control)) return;

  markSelectedControl(editorRoot, control);

  // اگر کنترل فوکوس‌پذیر است، به آن فوکوس بده
  if (
    control instanceof HTMLInputElement ||
    control instanceof HTMLTextAreaElement ||
    control instanceof HTMLSelectElement
  ) {
    control.focus();
  }
};

// export const syncSelectedControlWithSelection = (

export const syncSelectedControlWithSelection = (
  editorRoot: HTMLDivElement | null
) => {
  if (!editorRoot) return;

  const range = getSelectionRangeInEditorRoot(editorRoot);
  if (!range) {
    return;
  }

  // اگر range collapsed است (کلیک در یک نقطه)
  if (range.collapsed) {
    // بررسی مستقیم - آیا container خودش یک کنترل stylable است؟
    let container: Node | null = range.startContainer;

    while (container && container !== editorRoot) {
      if (isElementNode(container)) {
        const element = container as HTMLElement;
        // چک کردن اینکه آیا این المان یا والد نزدیکش یک کنترل stylable است
        const control = element.matches(STYLABLE_CONTROL_SELECTOR)
          ? element
          : (element.closest(STYLABLE_CONTROL_SELECTOR) as HTMLElement | null);

        if (control && editorRoot.contains(control)) {
          markSelectedControl(editorRoot, control);
          return;
        }
      }
      container = container.parentNode;
    }

    // اگر کنترل مستقیم پیدا نشد، neighbors را بررسی کن
    const nearControl = getControlNearCollapsedRange(range, editorRoot);
    if (nearControl) {
      markSelectedControl(editorRoot, nearControl);
      return;
    }
  }

  // برای range غیر collapsed
  const selectionNode = isElementNode(range.commonAncestorContainer)
    ? range.commonAncestorContainer
    : range.commonAncestorContainer.parentElement;
  const insideControl = getClosestStylableControl(selectionNode ?? null);

  if (insideControl && editorRoot.contains(insideControl)) {
    markSelectedControl(editorRoot, insideControl);
    return;
  }

  clearSelectedEditorControl(editorRoot);
};

export const getSelectedEditorControl = (
  editorRef: React.RefObject<HTMLDivElement>
) => {
  if (!editorRef.current) return null;

  const selected = editorRef.current.querySelector(
    `[${SELECTED_CONTROL_ATTRIBUTE}="true"]`
  ) as HTMLElement | null;

  if (!selected || !editorRef.current.contains(selected)) return null;

  return selected;
};

export const getEditorSelectionRange = (
  editorRef: React.RefObject<HTMLDivElement>
) => getSelectionRangeInEditorRoot(editorRef.current);

export const getCurrentStyleTarget = (
  editorRef: React.RefObject<HTMLDivElement>
) => {
  const range = getEditorSelectionRange(editorRef);

  const controlTarget = resolvePreferredControlTarget(editorRef, range);
  if (controlTarget && editorRef.current) {
    markSelectedControl(editorRef.current, controlTarget);
    return controlTarget;
  }

  if (range && !range.collapsed) {
    return isElementNode(range.commonAncestorContainer)
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;
  }

  if (!range) return null;

  return isElementNode(range.commonAncestorContainer)
    ? range.commonAncestorContainer
    : range.commonAncestorContainer.parentElement;
};

export const runEditorCommand = (
  editorRef: React.RefObject<HTMLDivElement>,
  command: string,
  value?: string
) => {
  const range = getEditorSelectionRange(editorRef);

  const controlTarget = resolvePreferredControlTarget(editorRef, range);
  if (controlTarget) {
    const applied = applyStyleOnControl(controlTarget, command, value);
    if (applied) {
      if (editorRef.current) {
        markSelectedControl(editorRef.current, controlTarget);
      }
      updateFitTextForControl(controlTarget);
      editorRef.current?.focus();
      return true;
    }
  }

  if (range && !range.collapsed) {
    runNativeCommand(command, value);
    editorRef.current?.focus();
    return true;
  }

  if (!range) return false;

  runNativeCommand(command, value);
  editorRef.current?.focus();
  return true;
};

export const getStyleCommandStateFromControl = (
  editorRef: React.RefObject<HTMLDivElement>,
  command: string
) => {
  const range = getEditorSelectionRange(editorRef);
  const selectedControl = resolvePreferredControlTarget(editorRef, range);
  if (!selectedControl) return null;

  if (editorRef.current) {
    markSelectedControl(editorRef.current, selectedControl);
  }

  const styles = window.getComputedStyle(selectedControl);

  switch (command) {
    case "bold":
      return isBoldWeight(styles.fontWeight);
    case "italic":
      return styles.fontStyle === "italic";
    case "underline":
      return getDecorationTokens(styles.textDecorationLine).includes("underline");
    case "strikeThrough":
      return getDecorationTokens(styles.textDecorationLine).includes("line-through");
    case "justifyLeft":
    case "justifyCenter":
    case "justifyRight":
    case "justifyFull":
      return getAlignState(styles.textAlign, command);
    default:
      return null;
  }
};

export const colorToHex = (color: string, fallback = "#000000") => {
  if (!color) return fallback;

  const normalized = color.trim().toLowerCase();

  if (normalized === "transparent") return fallback;

  if (/^#([0-9a-f]{3}){1,2}$/i.test(normalized)) {
    if (normalized.length === 4) {
      const [hash, r, g, b] = normalized;
      return `${hash}${r}${r}${g}${g}${b}${b}`;
    }

    return normalized;
  }

  const match = normalized.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
  );
  if (!match) return fallback;

  const alpha = typeof match[4] === "undefined" ? 1 : Number.parseFloat(match[4]);
  if (!Number.isNaN(alpha) && alpha === 0) return fallback;

  const toHex = (value: string) => {
    const parsed = Number.parseInt(value, 10);
    return parsed.toString(16).padStart(2, "0");
  };

  return `#${toHex(match[1])}${toHex(match[2])}${toHex(match[3])}`;
};
